/**
 * AI Service Abstraction Layer
 * Provides a unified interface for AI-powered diagram generation across multiple providers.
 */

import { GeneratedStack, AIGenerationResult, AIGenerationError, AIGenerationProgressEvent, AIProvider } from '../types';
import { getModel, buildRevisePrompt, buildPatchPrompt } from './aiBehavior';
import { applyPatchToStack, validatePatch } from './aiPatch';
import { validateAndCorrectStack } from './stackValidation';
import { getApiKey, getDefaultProvider, hasApiKey, getCustomBaseUrl, getCustomModel } from './apiKeyService';
import type { ExistingDiagramContext } from './geminiService';

// Re-export from apiKeyService for convenience
export { AI_PROVIDERS, type AIProviderConfig } from './apiKeyService';
export type { ExistingDiagramContext } from './geminiService';

/**
 * Interface for AI provider implementations
 */
export interface AIProviderImplementation {
  generateStack: (prompt: string, apiKey: string) => Promise<GeneratedStack>;
  testConnection: (apiKey: string) => Promise<boolean>;
}

/**
 * Check if any AI provider is configured
 */
export function isAIConfigured(): boolean {
  return hasApiKey('gemini') || hasApiKey('openai') || hasApiKey('anthropic') || hasApiKey('custom');
}

/**
 * Get the currently active AI provider
 */
export function getActiveProvider(): AIProvider | null {
  return getDefaultProvider();
}

/**
 * Create an AI generation error
 */
function createError(
  code: AIGenerationError['code'],
  message: string,
  provider: AIProvider
): AIGenerationError {
  return { code, message, provider };
}

interface ProviderSession {
  provider: AIProvider;
  apiKey: string;
  /** Custom-provider model id (e.g. the panel's OpenRouter selection); '' → saved selection. */
  model?: string;
}

interface ProgressOptions {
  onProgress?: (event: AIGenerationProgressEvent) => void;
  signal?: AbortSignal;
  /** Custom-provider model override (the panel's per-model selection). */
  model?: string;
}

/**
 * Resolve the provider + API key for a generation-style run, including the
 * custom-provider model guard. Shared by generate and revise.
 */
async function resolveProviderSession(
  preferredProvider?: AIProvider,
  preferredModel?: string
): Promise<{ session: ProviderSession } | { failure: AIGenerationResult }> {
  const provider = preferredProvider || getDefaultProvider();

  if (!provider) {
    return {
      failure: {
        success: false,
        error: createError(
          'NO_API_KEY',
          'No AI provider configured. Please add your API key in Settings.',
          'gemini' // Default to gemini for error context
        ),
      },
    };
  }

  const apiKey = await getApiKey(provider);

  if (!apiKey) {
    return {
      failure: {
        success: false,
        error: createError(
          'NO_API_KEY',
          `No API key found for ${provider}. Please add your key in Settings.`,
          provider
        ),
      },
    };
  }

  const model = preferredModel?.trim() || getCustomModel();

  if (provider === 'custom' && !model) {
    return {
      failure: {
        success: false,
        error: createError(
          'GENERATION_FAILED',
          'No model set for the custom provider. Add a model id (e.g. "openai/gpt-4o") in Settings → AI Configuration.',
          provider
        ),
      },
    };
  }

  return { session: { provider, apiKey, model } };
}

/**
 * Build the provider dispatcher. `baseOverride`, when given, is the prompt for
 * the initial call (revise mode) — per-call overrides (repair rounds) win.
 * `responseMode` threads the raw-JSON flag to providers (patch protocol).
 */
function createProviderCaller(
  session: ProviderSession,
  callArgs: {
    prompt: string;
    existingContext?: ExistingDiagramContext;
    baseOverride?: string;
    responseMode?: 'stack' | 'raw';
  },
  signal?: AbortSignal
): (promptOverride?: string) => Promise<GeneratedStack | null> {
  const { provider, apiKey } = session;
  const responseMode = callArgs.responseMode ?? 'stack';
  return async (promptOverride?: string): Promise<GeneratedStack | null> => {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const effectiveOverride = promptOverride ?? callArgs.baseOverride;
    if (provider === 'gemini') {
      const { generateTechStack } = await import('./geminiService');
      return generateTechStack(callArgs.prompt, apiKey, callArgs.existingContext, { promptOverride: effectiveOverride, signal, responseMode });
    }
    if (provider === 'openai') {
      const { generateTechStack } = await import('./openaiService');
      return generateTechStack(callArgs.prompt, apiKey, callArgs.existingContext, { promptOverride: effectiveOverride, signal, responseMode });
    }
    if (provider === 'anthropic') {
      const { generateTechStack } = await import('./claudeService');
      return generateTechStack(callArgs.prompt, apiKey, callArgs.existingContext, { promptOverride: effectiveOverride, signal, responseMode });
    }
    if (provider === 'custom') {
      const model = session.model || getCustomModel();
      if (!model) {
        return null;
      }
      // OpenAI-compatible endpoint (OpenRouter, etc.) — reuse the OpenAI path.
      const { generateTechStack } = await import('./openaiService');
      return generateTechStack(callArgs.prompt, apiKey, callArgs.existingContext, {
        baseUrl: getCustomBaseUrl(),
        model,
        promptOverride: effectiveOverride,
        signal,
        responseMode,
      });
    }
    return null;
  };
}

/**
 * Shared post-call flow for generation-style runs: progress events, the
 * bounded repair loop (re-layout + geometry validation, model fixes only
 * measured issues), abort handling, and provider error mapping.
 */
async function runStackPipeline(
  session: ProviderSession,
  callProvider: (promptOverride?: string) => Promise<GeneratedStack | null>,
  opts?: ProgressOptions
): Promise<AIGenerationResult> {
  const { provider } = session;
  const onProgress = opts?.onProgress;
  const signal = opts?.signal;

  try {
    let result: GeneratedStack | null = null;
    let quality: AIGenerationResult['quality'] | undefined;

    onProgress?.({ type: 'start' });
    result = await callProvider();

    if (result) {
      const modelNotes = result.notes;
      onProgress?.({ type: 'generated' });
      // Focused repair round: re-layout + geometry-validate, and let the model
      // fix only the measured issues (max 2 rounds, best score wins). Skips
      // itself automatically on clean or tiny diagrams.
      const { runRepairLoop } = await import('./aiRepair');
      const repaired = await runRepairLoop(
        result,
        (repairPrompt) => callProvider(repairPrompt),
        { onProgress }
      );
      result = repaired.stack;
      quality = {
        score: repaired.quality.score,
        repairRounds: repaired.quality.repairRounds,
        issues: repaired.quality.issues,
      };
      onProgress?.({ type: 'done', score: repaired.quality.score, repairRounds: repaired.quality.repairRounds });

      return {
        success: true,
        data: result,
        quality,
        notes: result.notes ?? modelNotes,
      };
    }

    return {
      success: false,
      error: createError(
        'GENERATION_FAILED',
        'Failed to generate diagram. Please try again.',
        provider
      ),
    };
  } catch (err) {
    // User pressed stop — not an error, callers reset quietly.
    if (signal?.aborted) {
      return { success: false, aborted: true };
    }
    // Parse error to determine type
    const errorMessage = err instanceof Error ? err.message : 'Unknown error';

    if (errorMessage.includes('API_KEY_INVALID') || errorMessage.includes('401')) {
      return {
        success: false,
        error: createError(
          'INVALID_API_KEY',
          'Your API key is invalid. Please check your key in Settings.',
          provider
        ),
      };
    }

    if (errorMessage.includes('RATE_LIMIT') || errorMessage.includes('429')) {
      return {
        success: false,
        error: createError(
          'RATE_LIMIT',
          'Rate limit exceeded. Please wait a moment and try again.',
          provider
        ),
      };
    }

    // AbortSignal.timeout throws TimeoutError; @google/genai surfaces 'timeout'.
    if (/timeout|timed out|aborted/i.test(errorMessage)) {
      return {
        success: false,
        error: createError(
          'TIMEOUT',
          'Generation timed out after 3 minutes. Try a shorter prompt or a faster model.',
          provider
        ),
      };
    }

    if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
      return {
        success: false,
        error: createError(
          'NETWORK_ERROR',
          'Network error. Please check your connection and try again.',
          provider
        ),
      };
    }

    return {
      success: false,
      error: createError(
        'GENERATION_FAILED',
        errorMessage || 'Failed to generate diagram. Please try again.',
        provider
      ),
    };
  }
}

/**
 * Generate a tech stack diagram using the configured AI provider
 * @param prompt - User's description of what to generate
 * @param preferredProvider - Optional preferred AI provider
 * @param existingContext - Optional existing diagram context for append mode
 * @param opts - Live progress callback (drives the modal's activity feed) and
 *   an AbortSignal wired to the composer's stop button.
 */
export async function generateTechStackWithAI(
  prompt: string,
  preferredProvider?: AIProvider,
  existingContext?: ExistingDiagramContext,
  opts?: ProgressOptions
): Promise<AIGenerationResult> {
  const resolved = await resolveProviderSession(preferredProvider, opts?.model);
  if ('failure' in resolved) return resolved.failure;

  const callProvider = createProviderCaller(
    resolved.session,
    { prompt, existingContext },
    opts?.signal
  );
  return runStackPipeline(resolved.session, callProvider, opts);
}

/**
 * Revise an existing diagram conversationally — PATCH-FIRST protocol.
 *
 * The model gets the current canvas (serialized via canvasToGeneratedStack)
 * plus the change request and returns only a GeneratedPatch (services/
 * aiPatch.ts validates + applies it onto the current stack); the patched
 * stack then flows through mergeRevisedStack (services/aiRevise.ts) which
 * preserves existing positions. Absence is a no-op, so unmentioned nodes can
 * never be accidentally deleted and responses stay small on large canvases.
 *
 * Fallbacks: an invalid or empty patch (and provider errors on the patch
 * call, aborts excluded) retry once with the proven full-stack revise
 * prompt. Repair rounds always speak the full-stack dialect. The model's
 * `notes` travel back as result.notes for the chat reply.
 */
export async function reviseDiagramWithAI(
  instruction: string,
  preferredProvider: AIProvider | undefined,
  currentStack: GeneratedStack,
  opts?: ProgressOptions
): Promise<AIGenerationResult> {
  const resolved = await resolveProviderSession(preferredProvider, opts?.model);
  if ('failure' in resolved) return resolved.failure;

  const currentStackJson = JSON.stringify(currentStack, null, 2);
  const patchCall = createProviderCaller(
    resolved.session,
    { prompt: instruction, baseOverride: buildPatchPrompt(instruction, currentStackJson), responseMode: 'raw' },
    opts?.signal
  );
  const fullCall = createProviderCaller(
    resolved.session,
    { prompt: instruction, baseOverride: buildRevisePrompt(instruction, currentStackJson) },
    opts?.signal
  );

  let notes: string | undefined;
  const callProvider = async (promptOverride?: string): Promise<GeneratedStack | null> => {
    // Repair rounds speak the full-stack dialect — route them straight there.
    if (promptOverride) return fullCall(promptOverride);
    try {
      const raw = await patchCall();
      if (raw) {
        const patch = validatePatch(raw);
        if (patch) {
          const applied = applyPatchToStack(currentStack, patch);
          if (applied.appliedOps > 0 || applied.notes) {
            const validation = validateAndCorrectStack(applied.stack);
            if (validation.isValid) {
              notes = applied.notes;
              return validation.correctedStack ?? applied.stack;
            }
            console.warn(
              'aiService: patch produced an invalid stack — falling back to full-stack revise',
              validation.errors
            );
          } else {
            console.warn('aiService: empty patch (no ops, no notes) — falling back to full-stack revise');
          }
        } else {
          console.warn('aiService: response is not a patch — falling back to full-stack revise');
        }
      }
    } catch (err) {
      // A user stop propagates; provider/network errors on the patch attempt
      // fall back to the full-stack path, which reports its own errors.
      if (opts?.signal?.aborted) throw err;
      console.warn('aiService: patch call failed — falling back to full-stack revise', err);
    }
    return fullCall();
  };

  const result = await runStackPipeline(resolved.session, callProvider, opts);
  if (result.success && notes) result.notes = notes;
  return result;
}

/**
 * Test if an AI provider's API key is valid
 */
export async function testAIConnection(provider: AIProvider, apiKey?: string, modelId?: string): Promise<boolean> {
  const key = apiKey || await getApiKey(provider);

  if (!key) return false;

  if (provider === 'gemini') {
    try {
      const { GoogleGenAI } = await import('@google/genai');
      const ai = new GoogleGenAI({ apiKey: key });

      const response = await ai.models.generateContent({
        model: getModel('gemini'),
        contents: 'Say "ok"',
        config: {
          maxOutputTokens: 5,
        },
      });

      return !!response.text;
    } catch {
      return false;
    }
  }

  if (provider === 'openai') {
    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${key}`,
        },
        body: JSON.stringify({
          model: getModel('openai'),
          messages: [{ role: 'user', content: 'Say "ok"' }],
          max_tokens: 5,
        }),
      });

      return response.ok;
    } catch {
      return false;
    }
  }

  if (provider === 'anthropic') {
    try {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': key,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
        body: JSON.stringify({
          model: getModel('anthropic'),
          max_tokens: 10,
          messages: [{ role: 'user', content: 'Say "ok"' }],
        }),
      });

      return response.ok;
    } catch {
      return false;
    }
  }

  if (provider === 'custom') {
    const model = modelId?.trim() || getCustomModel();
    if (!model) return false;
    try {
      const baseUrl = getCustomBaseUrl().replace(/\/+$/, '');
      const response = await fetch(`${baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${key}`,
          'HTTP-Referer': 'https://stacksketch.site',
          'X-Title': 'StackSketch',
        },
        body: JSON.stringify({
          model,
          messages: [{ role: 'user', content: 'Say "ok"' }],
          max_tokens: 5,
        }),
      });

      return response.ok;
    } catch {
      return false;
    }
  }

  return false;
}

/**
 * Extract simplified context from React Flow nodes for AI consumption
 * Filters to only include tech nodes and relevant data
 */
export function extractDiagramContext(
  nodes: Array<{ id: string; type?: string; data: Record<string, unknown> }>,
  edges: Array<{ source: string; target: string; sourceHandle?: string | null; targetHandle?: string | null }>
): ExistingDiagramContext {
  // Only include tech nodes (not groups, sticky notes, etc.)
  const techNodes = nodes
    .filter(n => n.type === 'techNode')
    .map(n => {
      const data = n.data as {
        label?: string;
        iconSlug?: string;
        icon?: string;
        description?: string;
        category?: string;
        importance?: number;
        groupId?: string;
      };
      return {
        id: n.id,
        label: (data.label as string) || 'Unknown',
        iconSlug: data.iconSlug || (data.icon?.startsWith('si:') ? data.icon.substring(3) : data.icon) || '',
        description: (data.description as string) || undefined,
        category: data.category as string,
        importance: data.importance as number,
        groupId: data.groupId as string,
      };
    });

  // Filter edges to only include connections between tech nodes
  const techNodeIds = new Set(techNodes.map(n => n.id));
  const relevantEdges = edges
    .filter(e => techNodeIds.has(e.source) || techNodeIds.has(e.target))
    .map(e => ({
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle,
      targetHandle: e.targetHandle,
    }));

  // Extract group information
  const groups = nodes
    .filter(n => n.type === 'groupNode' || n.type === 'group')
    .map(n => {
      const data = n.data as { label?: string; description?: string };
      return {
        id: n.id,
        label: data.label || 'Group',
        description: data.description,
      };
    });

  // Extract sticky notes
  const stickyNotes = nodes
    .filter(n => n.type === 'stickyNote')
    .map(n => {
      const data = n.data as { label?: string; color?: string };
      return {
        id: n.id,
        label: data.label || '',
        color: data.color || 'yellow',
      };
    });

  return {
    nodes: techNodes,
    edges: relevantEdges,
    groups,
    stickyNotes,
    // Add summary statistics
    summary: {
      totalNodes: techNodes.length,
      totalEdges: relevantEdges.length,
      nodeCategories: [...new Set(techNodes.map(n => n.category).filter(Boolean))],
      hasGroups: groups.length > 0,
      hasNotes: stickyNotes.length > 0,
    },
  };
}
